import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { Comment } from "./Comment/Comment";
import { CommentForm } from "./CommentForm/CommentForm";
import {
	HubCommentModel,
	HubCommentPagingInformationModel,
	HubCommentSaveModel,
	HubCommentStatisticsModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "client/Api/Hub/common";
import {
	AlertManager,
	NumberFormatter,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import styles from "./HubCommentSection.module.less";
import {
	ensureNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	MobileComment,
	MobileCommentMode
} from "./MobileComment/MobileComment";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	Anchor,
	InlineOptionSelectorItem,
	LoadMoreIndicator,
	Toolbar
} from "client/Components";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { formatUnit } from "client/Misc/cultureSpecificUtils";
import { SortDirection } from "client/Api/common";

export type HubCommentSectionHandle = {
	focusNewCommentInput(): void;
};

export type CommentWithRepliesModel = HubCommentModel & {
	replies?: readonly HubCommentModel[];
};

export type HubCommentSectionProps = {
	ref?: Ref<HTMLDivElement>;
};

export const HubCommentSection = (props: PropsWithChildren<HubCommentSectionProps>): ReactElement => {
	const {
		children,
		ref
	} = props;

	return (
		<div
			{...setTrackingId("comments-section")}
			className={styles.commentSection}
			ref={ref}
		>
			{children}
		</div>
	);
};
HubCommentSection.displayName = "HubCommentSection";

export type CommentListHandle = {
	scrollToComment: (commentId: number) => void;
};

export type CommentListProps = {
	comments: readonly CommentWithRepliesModel[];
	onStatisticsUpdated: (
		commentId: number,
		statistics: HubCommentStatisticsModel
	) => void;
	onUpdated: (comment: HubCommentModel) => void;
	onDeleted: (commentId: number) => void;
	onCreated: (comment: HubCommentModel) => void;
	isMobile: boolean;
	mobileModeChangeHandler: (newValue: MobileCommentMode) => void;
	mobileMode: MobileCommentMode;
	service: HubCommentSectionService;
	currentUserInfo: UserIdentityModel;
	timezone: TimeZone;
	viewImageHandler: (imageUrl: string) => void;
	commentOptions: HubCommentOptionsModel;
	renderAfterItems?: ReactElement | null;
};

const CommentList = forwardRef(
	(props: CommentListProps, ref: Ref<CommentListHandle>): ReactElement => {
		const {
			comments,
			onStatisticsUpdated,
			onUpdated,
			onDeleted,
			onCreated,
			isMobile,
			mobileModeChangeHandler,
			mobileMode: mobileMode,
			service,
			currentUserInfo,
			timezone,
			viewImageHandler,
			commentOptions,
			renderAfterItems
		} = props;

		const globalCancellation: AbortSignal = useUnmountedCancellation();
		const alertManager: AlertManager = useAlerts();
		const [commentIdToNavigateTo, setCommentIdToNavigateTo] = useState<number | undefined>(undefined);

		const sendReaction = useCallback(
			(reaction: ReactionKind, commentId: number): Promise<HubCommentStatisticsModel> => {
				const comment: HubCommentModel | undefined = findCommentById(comments, commentId);
				const commentStatistics: HubCommentStatisticsModel | undefined = comment?.statistics;
				const {
					registerCommentReaction,
					unregisterCommentReaction,
					updateCommentReaction
				} = service;

				const statistics: HubCommentStatisticsModel = ensureNotNil(commentStatistics);

				if (statistics.currentUserReaction === ReactionKind.NoReaction) {
					return registerCommentReaction(
						statistics.commentId,
						reaction,
						globalCancellation
					);
				}

				if (reaction === ReactionKind.NoReaction) {
					return unregisterCommentReaction(
						statistics.commentId,
						globalCancellation
					);
				}

				return updateCommentReaction(
					statistics.commentId,
					reaction,
					globalCancellation
				);
			},
			[
				comments,
				service,
				globalCancellation
			]
		);

		const reactionChangeHandler = useCallback(
			async (
				newReaction: ReactionKind,
				commentId: number
			): Promise<void> => {
				try {
					const newStat: HubCommentStatisticsModel = await sendReaction(newReaction, commentId);
					onStatisticsUpdated(
						commentId,
						newStat
					);
				} catch(e) {
					console.error(e);

					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[
				alertManager,
				onStatisticsUpdated,
				sendReaction
			]
		);

		useImperativeHandle(
			ref,
			(): CommentListHandle => ({
				scrollToComment: (commentId: number): void =>{
					setCommentIdToNavigateTo(commentId);
				}
			}),
			[]
		);

		return (
			<div
				className={styles.commentList}
				{...setTrackingId("comment-list")}
			>
				{comments.map(c => (
					<Comment
						key={c.id}
						comment={c}
						onReactionChange={reactionChangeHandler}
						onUpdated={onUpdated}
						onDeleted={onDeleted}
						onCreated={onCreated}
						isMobile={isMobile}
						onMobileCommentModeChange={mobileModeChangeHandler}
						replies={c.replies}
						commentIdToNavigateTo={commentIdToNavigateTo}
						resetNavigateCommentId={() => setCommentIdToNavigateTo(undefined)}
						mobileMode={mobileMode}
						service={service}
						currentUserInfo={currentUserInfo}
						timezone={timezone}
						onViewImage={viewImageHandler}
						options={commentOptions}
					/>
				))}
				{renderAfterItems}
			</div>
		);
	}
);
CommentList.displayName = "HubCommentSection.CommentList";

type CommentSectionHeaderProps = {
	sortPreference: CommentsSortPreference;
	onSortPreferenceChange: (sortPreference: CommentsSortPreference) => void;
	total: number;
};

const CommentSectionHeader = (props: CommentSectionHeaderProps): ReactElement => {
	const {
		sortPreference,
		onSortPreferenceChange,
		total
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const headerText: string = useMemo(
		(): string => {
			return formatUnit({
				value: total,
				singularForm: "_1Comment",
				pluralForm: "_NComments",
				valueFormatter: statisticsFormatter
			});
		},
		[statisticsFormatter, total]
	);

	return (
		<div className={styles.header}>
			<h4
				{...setTrackingId("comments-count")}
				className={styles.headerText}
			>
				{headerText}
			</h4>
			<div className={styles.sort}>
				<Toolbar horizontalAlignment="right">
					<Toolbar.InlineOptionSelector
						trackingId="comments-sort"
						value={sortPreference}
						onChange={onSortPreferenceChange}
						options={commentsSortOptions}
						menuTitle={localize("_SortBy")}
						dropOverAlignment="right"
					/>
				</Toolbar>
			</div>
		</div>
	);
};
CommentSectionHeader.displayName = "HubCommentSection.Header";

export type LoadMoreProps = {
	variant: "load-more-indicator" | "button";
	isLoading: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
};

const LoadMore = (props: LoadMoreProps): ReactElement | null => {
	const {
		hasMore,
		isLoading,
		onLoadMore,
		variant
	} = props;

	if (variant === "load-more-indicator" || isLoading) {
		return (
			<LoadMoreIndicator
				hasMoreItems={hasMore}
				isLoadingData={isLoading}
				onRequestMoreItems={onLoadMore}
			/>
		);
	}

	return hasMore
		? (
			<div className={styles.loadMoreContainer}>
				<Anchor
					onClick={onLoadMore}
				>
					{localize("_ShowMoreComments")}
				</Anchor>
			</div>
		)
		: null;
};
LoadMore.displayName = "HubCommentSection.LoadMore";

const NoComments = (): ReactElement => {
	return (
		<div className={styles.noCommentsPlaceholder}>
			{localize("_TheCommentsSectionIsTurnedOff")}
		</div>
	);
};
NoComments.displayName = "HubCommentSection.NoComments";

HubCommentSection.CommentForm = CommentForm;
HubCommentSection.MobileComment = MobileComment;
HubCommentSection.CommentList = CommentList;
HubCommentSection.Header = CommentSectionHeader;
HubCommentSection.LoadMore = LoadMore;
HubCommentSection.NoComments = NoComments;

export enum CommentsSortPreference {
	NewestOnTop = 0,
	OldestOnTop = 1
}

const commentsSortOptions: readonly InlineOptionSelectorItem<CommentsSortPreference>[] = [
	{
		text: localize("_NewestOnTop"),
		value: CommentsSortPreference.NewestOnTop,
		valueTrackingId: "newest-on-top"
	},
	{
		text: localize("_OldestOnTop"),
		value: CommentsSortPreference.OldestOnTop,
		valueTrackingId: "oldest-on-top"
	}
];

export type HubCommentSectionService = {
	createComment: (
		comment: HubCommentSaveModel,
		signal: AbortSignal
	) => Promise<HubCommentModel>;

	editComment: (
		commentId: number,
		comment: HubCommentSaveModel,
		signal: AbortSignal
	) => Promise<HubCommentModel>;

	createReply: (
		parentCommentId: number,
		newComment: HubCommentSaveModel,
		signal?: AbortSignal
	) => Promise<HubCommentModel>;

	deleteComment: (
		commentId: number,
		signal: AbortSignal
	) => Promise<void>;

	findUsers: (
		searchTerm: string,
		amount: number,
		signal: AbortSignal
	) => Promise<readonly UserIdentityModel[]>;

	doesUserHaveAccess: (
		userId: number,
		signal?: AbortSignal
	) => Promise<boolean>;

	registerCommentReaction: (
		commentId: number,
		kind: ReactionKind,
		signal: AbortSignal
	) => Promise<HubCommentStatisticsModel>;

	updateCommentReaction: (
		commentId: number,
		kind: ReactionKind,
		signal: AbortSignal
	) => Promise<HubCommentStatisticsModel>;

	unregisterCommentReaction: (
		commentId: number,
		signal: AbortSignal
	) => Promise<HubCommentStatisticsModel>;

	getCommentReactionCounts: (
		commentId: number,
		signal?: AbortSignal
	) => Promise<readonly ReactionCounterModel[]>;

	getCommentReactions: (
		commentId: number,
		pageSize: number,
		skip: number,
		kind: ReactionKind | undefined,
		signal?: AbortSignal
	) => Promise<LazyLoadPageResult<ReactionUserModel>>;

	getCommentImageUrl: (imageId: string) => string;

	getCommentsList: (
		amount: number | null,
		afterComment: HubCommentPagingInformationModel | null,
		sortDirection: SortDirection,
		signal?: AbortSignal
	) => Promise<LazyLoadPageResult<HubCommentModel>>;
};

function findCommentById(
	comments: readonly CommentWithRepliesModel[],
	id: number
): HubCommentModel | undefined {
	for (const comment of comments) {
		if (comment.id === id) {
			return comment;
		}

		if (comment.replies) {
			const found: HubCommentModel | undefined = findCommentById(comment.replies, id);
			if (found) {
				return found;
			};
		}
	}
	return undefined;
}